import ConstellationTimeline from './ConstellationTimeline.js'
import WorksCarousel from './WorksCarousel.js'

export default class PortfolioUI {
    constructor() {
        this.navContainer = document.querySelector('.nav-container');
        this.portfolioContainer = document.querySelector('.portfolio-container');
        this.scrollProgress = document.querySelector('.scroll-progress');
        this.progressBar = document.querySelector('.progress-bar');
        this.progressRocket = document.querySelector('.progress-rocket');
        this.navLinks = document.querySelectorAll('.nav-link');
        this.portfolioSections = document.querySelectorAll('.portfolio-section');
        this.scrollCue = document.querySelector('.scroll-cue');
        this.currentSection = 'home';
        this.isVisible = false;
        this.scrollRafPending = false;
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.constellation = null;

        this.init();
    }

    init() {
        this.setupNavigation();
        this.setupScrollProgress();
        this.setupMobileMenu();
        this.setupScrollReveals();
        this.setupFooterReturn();
        this.setupBookSync();
        this.setupFreeCameraSync();
        this.worksCarousel = new WorksCarousel();
        this.setupTvSync();
        this.hide(); // Initially hide the UI
    }

    // 3DのTVをタップして動画が切り替わったら、カルーセルを同期し Works へ追従スクロールする
    //（本タップ → Activity と同じ思想。イベントはユーザータップ起点のときだけ TV が発火する）
    setupTvSync() {
        document.addEventListener('tvVideoChanged', (event) => {
            if (!event.detail.isOn) return; // タップで電源 OFF になった時は何もしない

            this.worksCarousel?.show(event.detail.index);
            if (this.isVisible && this.currentSection !== 'works') {
                this.scrollToSection('works');
            }
        });
    }

    // デバッグ用フリーカメラ（Camera.setFreeCameraMode）の間は、2D UI 層がポインターイベントを
    // 吸うと OrbitControls を操作できないため、オーバーレイを素通しにして薄く表示する
    setupFreeCameraSync() {
        this.freeCameraActive = false;

        document.addEventListener('freeCameraChanged', (event) => {
            this.freeCameraActive = event.detail.enabled;

            if (this.portfolioContainer) {
                this.portfolioContainer.classList.toggle('free-camera', this.freeCameraActive);
                // show() が inline で pointerEvents を設定するため、ここも inline で上書きする
                this.portfolioContainer.style.pointerEvents =
                    this.freeCameraActive ? 'none' : (this.isVisible ? 'auto' : 'none');
            }
        });
    }

    // 3Dの本を直接クリックしてページが開かれたら、対応するセクションへ2D UIも追従スクロールする。
    // イベントはユーザー操作起点のときだけ Book が発火する（SceneDirector 起点では発火しない）
    setupBookSync() {
        // ページ番号 → セクション id（SceneDirector の focus 設定と対応させる）
        const pageSections = { 1: 'activity', 2: 'special-thanks' };

        document.addEventListener('bookPageChanged', (event) => {
            const sectionId = pageSections[event.detail.page];
            if (!sectionId || !this.isVisible || this.currentSection === sectionId) return;
            this.scrollToSection(sectionId);
        });
    }

    setupNavigation() {
        // Add click event listeners to nav links
        this.navLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                const targetSection = link.getAttribute('href').substring(1);
                this.scrollToSection(targetSection);
            });
        });

        // Setup intersection observer for section visibility
        const observerOptions = {
            root: null,
            rootMargin: '-50% 0px',
            threshold: 0
        };

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const sectionId = entry.target.id;
                    this.setActiveSection(sectionId);
                }
            });
        }, observerOptions);

        this.portfolioSections.forEach(section => {
            observer.observe(section);
        });
    }

    setupScrollProgress() {
        if (!this.portfolioContainer) return;

        this.portfolioContainer.addEventListener('scroll', () => {
            if (this.scrollRafPending) return;
            this.scrollRafPending = true;

            requestAnimationFrame(() => {
                this.scrollRafPending = false;

                const scrollTop = this.portfolioContainer.scrollTop;
                const scrollHeight = this.portfolioContainer.scrollHeight - this.portfolioContainer.clientHeight;
                const progress = scrollHeight > 0 ? scrollTop / scrollHeight : 0;

                // プログレスバーの::after疑似要素の高さを調整して、上から下に向かって透明にする
                if (this.progressBar) {
                    this.progressBar.style.setProperty('--progress-height', `${progress * 100}%`);
                }

                // ロケットの位置をプログレスに合わせて更新
                if (this.progressRocket) {
                    this.progressRocket.style.setProperty('--progress-height', `${progress * 100}%`);
                }

                // ヒーローを過ぎたらnavに背景、スクロールキューを隠す
                if (this.navContainer) {
                    this.navContainer.classList.toggle('scrolled', scrollTop > 40);
                }
                if (this.scrollCue) {
                    this.scrollCue.classList.toggle('is-hidden', progress > 0.05);
                }

                // 3D層（SceneDirector）へ連続スクロール量を通知
                document.dispatchEvent(new CustomEvent('scrollProgress', {
                    detail: {
                        progress,
                        section: this.currentSection,
                        sectionProgress: this.getSectionProgress(scrollTop)
                    }
                }));
            });
        });
    }

    getSectionProgress(scrollTop) {
        const section = document.getElementById(this.currentSection);
        if (!section) return 0;
        const start = section.offsetTop;
        const height = section.offsetHeight || 1;
        return Math.min(1, Math.max(0, (scrollTop - start) / height));
    }

    setupMobileMenu() {
        const navToggle = document.querySelector('.nav-toggle');
        const navMenu = document.querySelector('.nav-menu');

        if (navToggle && navMenu) {
            navToggle.addEventListener('click', () => {
                navToggle.classList.toggle('active');
                navMenu.classList.toggle('active');
            });

            // Close mobile menu when clicking on a link
            this.navLinks.forEach(link => {
                link.addEventListener('click', () => {
                    navToggle.classList.remove('active');
                    navMenu.classList.remove('active');
                });
            });
        }
    }

    // スクロールに合わせて [data-reveal] 要素をふわっと表示する
    setupScrollReveals() {
        const targets = document.querySelectorAll('[data-reveal]');
        if (!targets.length) return;

        if (this.reducedMotion.matches) {
            targets.forEach(el => el.classList.add('is-revealed'));
            return;
        }

        this.revealObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-revealed');
                    this.revealObserver.unobserve(entry.target);
                }
            });
        }, {
            root: null,
            rootMargin: '0px 0px -10% 0px',
            threshold: 0.15
        });

        targets.forEach(el => this.revealObserver.observe(el));
    }

    setupFooterReturn() {
        const footerReturn = document.querySelector('.footer-return');
        if (footerReturn) {
            footerReturn.addEventListener('click', () => {
                this.scrollToSection('home');
            });
        }
    }

    scrollToSection(sectionId) {
        const targetSection = document.getElementById(sectionId);
        if (targetSection && this.portfolioContainer) {
            const offsetTop = targetSection.offsetTop - 70; // Account for fixed nav height
            this.portfolioContainer.scrollTo({
                top: offsetTop,
                behavior: this.reducedMotion.matches ? 'auto' : 'smooth'
            });
        }
    }

    setActiveSection(sectionId) {
        if (this.currentSection === sectionId) return;

        this.currentSection = sectionId;

        // Update active nav link
        this.navLinks.forEach(link => {
            link.classList.remove('active');
            if (link.getAttribute('href') === `#${sectionId}`) {
                link.classList.add('active');
            }
        });

        // Update active section
        this.portfolioSections.forEach(section => {
            section.classList.remove('active');
            if (section.id === sectionId) {
                section.classList.add('active');
            }
        });

        // Dispatch custom event for section change
        document.dispatchEvent(new CustomEvent('sectionChanged', {
            detail: { section: sectionId }
        }));
    }

    //TODO: 表示したいタイミングで呼び出す。例えば、カメラが下にたどり着いた時とか。
    show() {
        if (this.isVisible) return;

        this.isVisible = true;

        // Show navigation with animation
        if (this.navContainer) {
            this.navContainer.style.opacity = '1';
            this.navContainer.style.visibility = 'visible';
            this.navContainer.style.transform = 'translateY(0)';
        }

        // Show portfolio container with animation
        if (this.portfolioContainer) {
            this.portfolioContainer.style.opacity = '1';
            this.portfolioContainer.style.visibility = 'visible';
            this.portfolioContainer.style.transform = 'translateY(0)';
            // フリーカメラ中は OrbitControls 操作のためポインターイベントを渡さない
            this.portfolioContainer.style.pointerEvents = this.freeCameraActive ? 'none' : 'auto';
        }

        // Show scroll progress
        if (this.scrollProgress) {
            this.scrollProgress.style.opacity = '1';
            this.scrollProgress.style.visibility = 'visible';
        }

        // Activate the first section
        setTimeout(() => {
            this.setActiveSection('home');
        }, 300);

        // 星座タイムライン（シグネチャー演出）— コンテナ表示後にレイアウトを測定して構築
        if (!this.constellation) {
            this.constellation = new ConstellationTimeline();
        }
    }

    hide() {
        if (!this.isVisible) return;

        this.isVisible = false;

        // Hide navigation
        if (this.navContainer) {
            this.navContainer.style.opacity = '0';
            this.navContainer.style.visibility = 'hidden';
            this.navContainer.style.transform = 'translateY(-100%)';
        }

        // Hide portfolio container
        if (this.portfolioContainer) {
            this.portfolioContainer.style.pointerEvents = 'none';
        }

        // Hide scroll progress
        if (this.scrollProgress) {
            this.scrollProgress.style.opacity = '0';
            this.scrollProgress.style.visibility = 'hidden';
        }

        // Deactivate all sections
        this.portfolioSections.forEach(section => {
            section.classList.remove('active');
        });
    }

    // Method to update hero buttons functionality
    updateHeroButtons() {
        const recordsBtn = document.querySelector('.btn-ghost');

        if (recordsBtn) {
            recordsBtn.addEventListener('click', () => {
                this.scrollToSection('works');
            });
        }
    }

    // Method to get current section
    getCurrentSection() {
        return this.currentSection;
    }

    // Method to check if UI is visible
    isUIVisible() {
        return this.isVisible;
    }
}
